package co.maytes.standupbot.dashboard.features.standupruns

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.SectionCard
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.standuprun.RunDraftDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunStatusDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay

private val DRAFT_STATUSES = setOf(StandupRunStatusDto.DRAFTED, StandupRunStatusDto.POSTED, StandupRunStatusDto.SKIPPED)
private const val AVATAR_SIZE_PX = 28

@Composable
fun RunDraftsSection(
    store: StandupRunsStore?,
    workspaceId: String,
    runId: String,
    status: StandupRunStatusDto,
) {
    var drafts by remember(runId) { mutableStateOf<List<RunDraftDto>>(emptyList()) }
    LaunchedEffect(store, workspaceId, runId, status) {
        if (store == null || workspaceId.isBlank() || status !in DRAFT_STATUSES) return@LaunchedEffect
        while (true) {
            (store.fetchDrafts(workspaceId, runId) as? Result.Ok)?.let { drafts = it.value.drafts }
            if (status != StandupRunStatusDto.DRAFTED) break
            delay(RunStatusRules.DRAFT_POLL_INTERVAL_MS)
        }
    }
    val groups = remember(drafts) { DraftPanelRules.groups(drafts) }
    if (!groups.isEmpty) {
        DraftsPanel(groups = groups, posted = status != StandupRunStatusDto.DRAFTED)
    }
}

@Composable
fun DraftsPanel(
    groups: DraftPanelRules.Groups,
    posted: Boolean,
) {
    SectionCard(
        title = "Reviews",
        description = DraftPanelRules.summary(groups, posted),
    ) {
        Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(Spacing.md)) {
            DraftGroup("Responded", groups.responded, AvatarTone.Accent)
            DraftGroup(DraftPanelRules.waitingHeading(posted), groups.waiting, AvatarTone.Muted)
            DraftGroup("Couldn't be reached on Slack", groups.undelivered, AvatarTone.Muted)
        }
    }
}

@Composable
private fun DraftGroup(
    heading: String,
    drafts: List<RunDraftDto>,
    tone: AvatarTone,
) {
    if (drafts.isEmpty()) return
    Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(Spacing.xs)) {
        SpanText(
            text = "$heading (${drafts.size})",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary),
        )
        drafts.forEach { draft ->
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("gap", "${Spacing.sm}") },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Avatar(name = draft.displayName.ifBlank { "?" }, sizePx = AVATAR_SIZE_PX, tone = tone)
                SpanText(
                    text = draft.displayName.ifBlank { "Unknown" },
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .color(Palette.textPrimary)
                        .styleModifier {
                            property("flex", "1 1 auto")
                            property("min-width", "0")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        },
                )
                SpanText(
                    text = DraftPanelRules.stateLabel(draft.state),
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textSecondary)
                        .margin(left = Spacing.xs),
                )
            }
        }
    }
}
